import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterBar = ({ filters, onFilterChange, onClearFilters, activeFiltersCount }) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'expired', label: 'Expired' },
    { value: 'draft', label: 'Draft' },
  ];

  const contractTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'service-agreement', label: 'Service Agreement' },
    { value: 'nda', label: 'Non-Disclosure Agreement' },
    { value: 'employment', label: 'Employment Contract' },
    { value: 'vendor', label: 'Vendor Agreement' },
    { value: 'licensing', label: 'Licensing Agreement' },
  ];

  const attorneyOptions = [
    { value: '', label: 'All Attorneys' },
    { value: 'sarah-johnson', label: 'Sarah Johnson' },
    { value: 'michael-chen', label: 'Michael Chen' },
    { value: 'emily-davis', label: 'Emily Davis' },
    { value: 'robert-wilson', label: 'Robert Wilson' },
    { value: 'lisa-anderson', label: 'Lisa Anderson' },
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const getActiveFilterChips = () => {
    const chips = [];
    
    if (filters?.status) {
      const statusLabel = statusOptions?.find(opt => opt?.value === filters?.status)?.label;
      chips?.push({ key: 'status', label: `Status: ${statusLabel}`, value: filters?.status });
    }
    
    if (filters?.type) {
      const typeLabel = contractTypeOptions?.find(opt => opt?.value === filters?.type)?.label;
      chips?.push({ key: 'type', label: `Type: ${typeLabel}`, value: filters?.type });
    }
    
    if (filters?.attorney) {
      const attorneyLabel = attorneyOptions?.find(opt => opt?.value === filters?.attorney)?.label;
      chips?.push({ key: 'attorney', label: `Attorney: ${attorneyLabel}`, value: filters?.attorney });
    }
    
    if (filters?.dateRange) {
      const dateLabel = dateRangeOptions?.find(opt => opt?.value === filters?.dateRange)?.label;
      chips?.push({ key: 'dateRange', label: `Date: ${dateLabel}`, value: filters?.dateRange });
    }

    return chips;
  };

  const removeFilter = (key) => {
    handleFilterChange(key, '');
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-legal p-6 mb-6">
      {/* Main Filter Row */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Filters</span>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 flex-1">
          <div className="min-w-[140px]">
            <Select
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
              placeholder="Status"
            />
          </div>

          <div className="min-w-[160px]">
            <Select
              options={contractTypeOptions}
              value={filters?.type}
              onChange={(value) => handleFilterChange('type', value)}
              placeholder="Contract Type"
            />
          </div>

          <div className="min-w-[140px]">
            <Select
              options={attorneyOptions}
              value={filters?.attorney}
              onChange={(value) => handleFilterChange('attorney', value)}
              placeholder="Attorney"
            />
          </div>

          <div className="min-w-[120px]">
            <Select
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
              placeholder="Date Range"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            iconName={isAdvancedOpen ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
          >
            Advanced
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      {/* Advanced Filters */}
      {isAdvancedOpen && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label="Contract Value (Min)"
              type="number"
              placeholder="0"
              value={filters?.minValue || ''}
              onChange={(e) => handleFilterChange('minValue', e?.target?.value)}
            />
            
            <Input
              label="Contract Value (Max)"
              type="number"
              placeholder="1000000"
              value={filters?.maxValue || ''}
              onChange={(e) => handleFilterChange('maxValue', e?.target?.value)}
            />

            <Input
              label="Client Name"
              type="text"
              placeholder="Search clients..."
              value={filters?.client || ''}
              onChange={(e) => handleFilterChange('client', e?.target?.value)}
            />

            <Input
              label="Contract ID"
              type="text"
              placeholder="CT-2024-..."
              value={filters?.contractId || ''}
              onChange={(e) => handleFilterChange('contractId', e?.target?.value)}
            />
          </div>

          {filters?.dateRange === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Start Date"
                type="date"
                value={filters?.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
              />
              
              <Input
                label="End Date"
                type="date"
                value={filters?.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
              />
            </div>
          )}
        </div>
      )}
      {/* Active Filter Chips */}
      {getActiveFilterChips()?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {getActiveFilterChips()?.map((chip) => (
            <div
              key={chip?.key}
              className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
            >
              <span>{chip?.label}</span>
              <button
                onClick={() => removeFilter(chip?.key)}
                className="hover:bg-primary/20 rounded-full p-0.5 transition-legal-fast"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;